import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Trophy, MapPin, UserPlus, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { tribuBanners } from "@/assets/tribus";
import { useAuth } from "@/components/auth/AuthProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TribuCardProps {
  tribu: {
    id: string;
    deporte: string;
    ciudad: string;
    descripcion?: string;
    banner_url?: string;
    xp_total: number;
    nivel: number;
    total_miembros: number;
    es_destacada: boolean;
    slug: string;
  };
}

export const TribuCard = ({ tribu }: TribuCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const bannerImage = tribuBanners[tribu.slug];

  // Sport-specific gradients for better fallback
  const sportGradients: Record<string, string> = {
    'running': 'from-orange-500 via-red-500 to-pink-500',
    'yoga': 'from-purple-400 via-pink-400 to-rose-400',
    'futbol': 'from-green-600 via-emerald-500 to-teal-500',
    'football': 'from-green-600 via-emerald-500 to-teal-500',
    'soccer': 'from-green-600 via-emerald-500 to-teal-500',
    'padel': 'from-blue-500 via-cyan-500 to-teal-400',
    'crossfit': 'from-red-600 via-orange-600 to-yellow-500',
    'ciclismo': 'from-yellow-500 via-orange-500 to-red-500',
    'cycling': 'from-yellow-500 via-orange-500 to-red-500',
    'natacion': 'from-blue-400 via-cyan-400 to-teal-400',
    'swimming': 'from-blue-400 via-cyan-400 to-teal-400',
    'boxeo': 'from-red-700 via-red-600 to-orange-600',
    'boxing': 'from-red-700 via-red-600 to-orange-600',
    'volleyball': 'from-yellow-400 via-amber-400 to-orange-400',
    'escalada': 'from-gray-600 via-stone-500 to-amber-600',
    'climbing': 'from-gray-600 via-stone-500 to-amber-600',
    'surf': 'from-cyan-500 via-blue-500 to-indigo-500',
    'gimnasio': 'from-slate-700 via-gray-600 to-zinc-600',
    'gym': 'from-slate-700 via-gray-600 to-zinc-600',
    'tango': 'from-rose-600 via-red-600 to-pink-600',
    'lucha': 'from-yellow-600 via-amber-600 to-orange-700',
  };

  // Find matching sport gradient
  const sportKey = Object.keys(sportGradients).find(key => 
    tribu.deporte.toLowerCase().includes(key)
  );
  const gradientClass = sportKey ? sportGradients[sportKey] : 'from-primary via-accent to-primary';

  const unirseTribuMutation = useMutation({
    mutationFn: async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      const { error } = await supabase
        .from('miembros_tribu')
        .insert({
          tribu_id: tribu.id,
          usuario_id: user.id,
          rol: 'miembro',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "¡Te has unido a la tribu! 🎉",
        description: `Ahora eres miembro de ${tribu.deporte} en ${tribu.ciudad}`,
      });
      queryClient.invalidateQueries({ queryKey: ['tribus'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error al unirse",
        description: error.message.includes('duplicate') 
          ? 'Ya eres miembro de esta tribu'
          : 'Hubo un problema al unirse a la tribu',
        variant: "destructive",
      });
    },
  });

  const handleJoinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    unirseTribuMutation.mutate();
  };

  return (
    <Card 
      className="group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] overflow-hidden border-2 hover:border-primary/50 bg-card"
      onClick={() => navigate(`/tribus/${tribu.slug}`)}
    >
      {/* Image Section with Overlay */}
      <div className="relative h-56 overflow-hidden">
        {bannerImage ? (
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
            style={{ 
              backgroundImage: `url(${bannerImage})`,
            }}
          />
        ) : (
          <>
            {/* Gradient fallback with pattern */}
            <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass}`} />
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
            {/* Sport icon in center for tribes without images */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Trophy className="w-20 h-20 text-white/30" />
            </div>
          </>
        )}
        
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60" />
        
        {/* Destacada Badge */}
        {tribu.es_destacada && (
          <Badge className="absolute top-4 right-4 bg-yellow-500 text-white hover:bg-yellow-600 border-2 border-white shadow-xl animate-pulse z-10 font-bold">
            <Sparkles className="w-3 h-3 mr-1" />
            Destacada
          </Badge>
        )}
        
        {/* Sport Name Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-3xl font-bold text-white drop-shadow-2xl mb-2">
            {tribu.deporte}
          </h3>
          <div className="flex items-center gap-2 text-white/90">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-semibold drop-shadow-lg">{tribu.ciudad}</span>
          </div>
          
          {/* Stats Badges */}
          <div className="flex gap-2 mt-3">
            <Badge className="bg-white text-gray-900 font-bold shadow-lg border-2 border-white/80">
              <Trophy className="w-3 h-3 mr-1 text-yellow-500" />
              Nivel {tribu.nivel}
            </Badge>
            <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold shadow-lg border-2 border-white/50">
              {tribu.xp_total} XP
            </Badge>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="p-4">
        {tribu.descripcion && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
            {tribu.descripcion}
          </p>
        )}

        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Miembros</p>
              <p className="text-lg font-bold text-foreground">{tribu.total_miembros}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">XP Total</p>
              <p className="text-lg font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                {tribu.xp_total}
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={handleJoinClick}
          disabled={unirseTribuMutation.isPending}
          className="w-full gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-all hover:scale-[1.02] shadow-lg font-semibold h-11"
        >
          {unirseTribuMutation.isPending ? (
            <>
              <Sparkles className="w-4 h-4 animate-spin" />
              Uniéndote...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Unirse a la Tribu
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
